/**
 * Serverless GPU Functions Page
 * 
 * Dashboard for managing serverless GPU functions and invocations.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Code2,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronRight,
  Terminal,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  serverlessFunctionsApi,
  serverlessInvocationsApi,
  ServerlessFunction,
  FunctionInvocation,
} from '../lib/api';

const statusColors: Record<string, string> = {
  // Build status
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  building: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  ready: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  // Invocation status
  queued: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  running: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  timeout: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'ready':
    case 'completed':
      return <CheckCircle2 className="w-4 h-4" />;
    case 'failed':
      return <XCircle className="w-4 h-4" />;
    case 'building':
    case 'running':
      return <Loader2 className="w-4 h-4 animate-spin" />;
    case 'queued':
    case 'pending':
      return <Clock className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

export default function ServerlessPage() {
  const [functions, setFunctions] = useState<ServerlessFunction[]>([]);
  const [invocations, setInvocations] = useState<FunctionInvocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFunction, setExpandedFunction] = useState<string | null>(null);
  const [selectedLogs, setSelectedLogs] = useState<{ id: string; logs: string } | null>(null);

  const loadData = async () => {
    try {
      const [funcs, invs] = await Promise.all([
        serverlessFunctionsApi.list(),
        serverlessInvocationsApi.list(),
      ]);
      setFunctions(funcs);
      setInvocations(invs);
    } catch (error) {
      console.error('Failed to load serverless data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Poll for updates every 10 seconds
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this function?')) return;
    try {
      await serverlessFunctionsApi.delete(id);
      toast.success('Function deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete function');
    }
  };

  const handleRebuild = async (id: string) => {
    try {
      await serverlessFunctionsApi.triggerBuild(id);
      toast.success('Build triggered');
      loadData();
    } catch (error) {
      toast.error('Failed to trigger build');
    }
  };

  const handleViewLogs = async (invocationId: string) => {
    try {
      const logs = await serverlessInvocationsApi.getLogs(invocationId);
      setSelectedLogs({ id: invocationId, logs });
    } catch (error) {
      toast.error('Failed to load logs');
    }
  };

  const stats = {
    totalFunctions: functions.length,
    readyFunctions: functions.filter((f) => f.buildStatus === 'ready').length,
    totalInvocations: invocations.length,
    runningInvocations: invocations.filter((i) => i.status === 'running').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gpu-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold gradient-text">Serverless GPU</h1>
          <p className="text-sm sm:text-base text-surface-400 mt-1">
            Deploy and run GPU functions with a simple Python decorator
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-surface-800 hover:bg-surface-700 
                     rounded-lg text-surface-300 transition-colors self-start sm:self-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface-900 rounded-xl p-6 border border-surface-800"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gpu-500/20 flex items-center justify-center">
              <Code2 className="w-5 h-5 text-gpu-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Functions</p>
              <p className="text-2xl font-bold">{stats.totalFunctions}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-surface-900 rounded-xl p-6 border border-surface-800"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Ready</p>
              <p className="text-2xl font-bold">{stats.readyFunctions}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-surface-900 rounded-xl p-6 border border-surface-800"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Play className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Invocations</p>
              <p className="text-2xl font-bold">{stats.totalInvocations}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-surface-900 rounded-xl p-6 border border-surface-800"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Running</p>
              <p className="text-2xl font-bold">{stats.runningInvocations}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Start */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-surface-800">
          <h2 className="text-lg font-semibold">Quick Start</h2>
        </div>
        <div className="p-6">
          <pre className="bg-surface-950 rounded-lg p-4 overflow-x-auto text-sm">
            <code className="text-surface-300">
{`from gpu_platform import App

app = App("my-project", api_key="your-api-key")

@app.function(gpu="g5.xlarge", requirements=["torch"])
def train_model(data):
    import torch
    # Your GPU code here
    return {"result": "success"}

app.deploy()
result = train_model.remote(my_data).result()`}
            </code>
          </pre>
          <p className="text-surface-400 text-sm mt-4">
            Install the SDK: <code className="text-gpu-400">pip install gpu-platform</code>
          </p>
        </div>
      </motion.div>

      {/* Functions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-surface-900 rounded-xl border border-surface-800"
      >
        <div className="px-6 py-4 border-b border-surface-800">
          <h2 className="text-lg font-semibold">Deployed Functions</h2>
        </div>
        
        {functions.length === 0 ? (
          <div className="p-12 text-center text-surface-400">
            <Code2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No functions deployed yet</p>
            <p className="text-sm mt-2">Use the Python SDK to deploy your first function</p>
          </div>
        ) : (
          <div className="divide-y divide-surface-800">
            {functions.map((func) => (
              <div key={func.id} className="p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedFunction(expandedFunction === func.id ? null : func.id)}
                >
                  <div className="flex items-center gap-4">
                    <button className="text-surface-400">
                      {expandedFunction === func.id ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-medium text-surface-200">
                          {func.name}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs border flex items-center gap-1 ${
                            statusColors[func.buildStatus]
                          }`}
                        >
                          <StatusIcon status={func.buildStatus} />
                          {func.buildStatus}
                        </span>
                      </div>
                      <p className="text-sm text-surface-500 mt-1">
                        {func.gpuType} • Python {func.pythonVersion} • Timeout: {func.timeoutSeconds}s
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRebuild(func.id);
                      }}
                      className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400"
                      title="Rebuild"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(func.id);
                      }}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-surface-400 
                                 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {expandedFunction === func.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 ml-9"
                  >
                    <div className="bg-surface-800 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-surface-500">Version:</span>
                          <span className="ml-2">{func.version}</span>
                        </div>
                        <div>
                          <span className="text-surface-500">Memory:</span>
                          <span className="ml-2">{func.memoryMb} MB</span>
                        </div>
                        <div>
                          <span className="text-surface-500">Created:</span>
                          <span className="ml-2">
                            {new Date(func.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-surface-500">Hash:</span>
                          <span className="ml-2 font-mono text-xs">{func.sourceHash}</span>
                        </div>
                      </div>
                      {func.buildLogs && (
                        <div className="mt-4">
                          <p className="text-sm text-surface-500 mb-2">Build Logs:</p>
                          <pre className="bg-surface-950 rounded p-3 text-xs overflow-x-auto max-h-40">
                            {func.buildLogs}
                          </pre>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Recent Invocations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-surface-900 rounded-xl border border-surface-800"
      >
        <div className="px-6 py-4 border-b border-surface-800">
          <h2 className="text-lg font-semibold">Recent Invocations</h2>
        </div>
        
        {invocations.length === 0 ? (
          <div className="p-12 text-center text-surface-400">
            <Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No invocations yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-surface-500 text-sm border-b border-surface-800">
                  <th className="px-6 py-3">ID</th>
                  <th className="px-6 py-3">Function</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Instance</th>
                  <th className="px-6 py-3">Duration</th>
                  <th className="px-6 py-3">Cost</th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-800">
                {invocations.slice(0, 10).map((inv) => {
                  const func = functions.find((f) => f.id === inv.functionId);
                  return (
                    <tr key={inv.id} className="hover:bg-surface-800/50">
                      <td className="px-6 py-4 font-mono text-sm">
                        {inv.id.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4">
                        {func?.name || inv.functionId.slice(0, 8)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs border flex items-center gap-1 w-fit ${
                            statusColors[inv.status]
                          }`}
                        >
                          <StatusIcon status={inv.status} />
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-surface-400">
                        {inv.instanceType}
                      </td>
                      <td className="px-6 py-4 text-surface-400">
                        {inv.durationMs ? `${(inv.durationMs / 1000).toFixed(1)}s` : '-'}
                      </td>
                      <td className="px-6 py-4 text-surface-400">
                        {inv.computeCostUsd ? `$${inv.computeCostUsd.toFixed(4)}` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleViewLogs(inv.id)}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-surface-400"
                          title="View Logs"
                        >
                          <Terminal className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Logs Modal */}
      {selectedLogs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface-900 rounded-xl border border-surface-800 w-full max-w-3xl max-h-[80vh] overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-surface-800 flex items-center justify-between">
              <h3 className="font-semibold">Invocation Logs</h3>
              <button
                onClick={() => setSelectedLogs(null)}
                className="text-surface-400 hover:text-surface-200"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[60vh]">
              <pre className="bg-surface-950 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap">
                {selectedLogs.logs || 'No logs available'}
              </pre>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
